import * as HLF from "html-living-framework";
import * as SpringRoll from "springroll";

import type { BaseCaptionRenderer } from "../GameObjects/BaseCaptionRenderer";
import type { CaptionToken } from "../Data/CaptionToken";

export class CaptionManager extends PIXI.Container {
    private _active: boolean;
    private readonly _data: SpringRoll.CaptionData;
    private _player: SpringRoll.CaptionPlayer | undefined;
    private _renderer: BaseCaptionRenderer | undefined;

    constructor(pData: SpringRoll.CaptionData) {
        super();
        this._active = true;
        this._data = pData;

        PubSub.subscribe(HLF.PLAY_CAPTION, this.onPlayCaptionRequested.bind(this));
        PubSub.subscribe(HLF.STOP_CAPTION, this.onStopCaptionRequested.bind(this));
    }

    public get active(): boolean {
        return this._active;
    }

    public set active(pValue: boolean) {
        this._active = pValue;
        if (this._renderer !== undefined) {
            this._renderer.visible = this._active;
        }
    }

    public setRenderer(pRenderer: BaseCaptionRenderer): void {
        if (this._renderer !== undefined) {
            this.removeChild(this._renderer);
        }
        this._renderer = pRenderer;
        this._renderer.visible = this._active;
        this.addChild(this._renderer);
        if (this._player === undefined) {
            this._player = new SpringRoll.CaptionPlayer(this._data, this._renderer);
        }
        else {
            this._player.renderer = this._renderer;
        }
    }

    public update(pDelta: number): void {
        if (this._player !== undefined && this._active === true) {
            this._player.update(pDelta);
        }
    }

    public playCaption(pId: string, pStart: number = 0, pArgs?: any): void {
        if (this._player !== undefined) {
            this._player.start(pId, pStart, pArgs);
        }
    }

    public stopCaption(): void {
        if (this._player !== undefined) {
            this._player.stop();
        }
    }

    public onResize(pSize: PIXI.Point): void {
        if (this._renderer !== undefined) {
            this._renderer.onResize(pSize);
        }
    }

    private onPlayCaptionRequested(pTopic: string, pToken: CaptionToken): void {
        if (pToken.args) {
            if (this._player!.captions![pToken.id]) {
                const lastLine = this._player!.captions![pToken.id].lines[this._player!.captions![pToken.id].lines.length - 1];
                // if token has args, loop through them
                const keys = Object.keys(pToken.args);
                for (let i = 0; i < keys.length; i++) {
                    // if content of variable is a caption id, add that caption's duration to this one
                    lastLine.endTime += this.getCaptionDuration(pToken.args[keys[i]]);

                    // if content of variable is a caption id, replace with its own text
                    pToken.args[keys[i]] = this.getCaptionText(pToken.args[keys[i]]);
                }
            }
            else {
                console.warn("playCaption No caption in json for key: " + pToken.id)
            }
        }
        this.playCaption(pToken.id, pToken.start, pToken.args);
    }

    private onStopCaptionRequested(pTopic: string, pToken: CaptionToken): void {
        this.stopCaption();
    }

    private getCaptionText(pId: string) {
        if (this._data[pId]) {
            return this._data[pId][0].content ?? pId;
        }
        else {
            return pId;
        }
    }

    private getCaptionDuration(pId: string) {
        if (this._data[pId]) {
            return this._data[pId][0].end ?? 0;
        }
        else {
            return 0;
        }
    }
}
